import React, { useState } from 'react';
import {
  X,
  Sparkles,
  Check,
  Zap,
  Cloud,
  Key,
  Save,
  Share2,
  TrendingUp,
  Crown,
  ArrowLeft
} from 'lucide-react';
import { SUBSCRIPTION_BENEFITS } from '../services/subscription';
import CheckoutForm from './CheckoutForm';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: string;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose, feature }) => {
  const [showCheckout, setShowCheckout] = useState(false);
  
  if (!isOpen) return null;

  const handleUpgrade = () => {
    setShowCheckout(true);
  };
  
  const handlePaymentSuccess = () => {
    // Reload the page to update subscription status
    window.location.reload();
  };
  
  const handleBack = () => {
    setShowCheckout(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 p-6 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3 mb-3">
            <div className="p-3 bg-white/20 rounded-xl">
              <Crown className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Upgrade to Pro</h2>
              <p className="text-white/90">Unlock unlimited possibilities</p>
            </div>
          </div>
          
          {feature && (
            <div className="mt-4 p-3 bg-white/10 rounded-lg">
              <p className="text-sm">
                <span className="font-semibold">🔒 Pro Feature Required:</span> {feature}
              </p>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[60vh]">
          {showCheckout ? (
            /* Checkout Form */
            <div>
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200 mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to features</span>
              </button>
              <CheckoutForm 
                planType="pro_monthly"
                onSuccess={handlePaymentSuccess}
                onCancel={handleBack}
              />
            </div>
          ) : (
          <>
          {/* Pricing */}
          <div className="text-center mb-6">
            <div className="inline-flex items-baseline gap-1">
              <span className="text-4xl font-bold text-gray-900 dark:text-white">$9</span>
              <span className="text-gray-600 dark:text-gray-400">/month</span>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              or $99/year (save 2 months!)
            </p>
          </div>

          {/* Benefits */}
          <div className="space-y-3 mb-6">
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Everything in Pro
            </h3>
            
            <div className="grid gap-2">
              {[
                { icon: Save, text: 'Save unlimited prompts to cloud', highlight: true },
                { icon: Key, text: 'No API keys required - we handle it', highlight: true },
                { icon: Cloud, text: 'Access from any device' },
                { icon: Share2, text: 'Share prompts with community' },
                { icon: Zap, text: 'Priority AI processing' },
                { icon: TrendingUp, text: 'Advanced analytics & insights' },
              ].map((benefit, index) => (
                <div 
                  key={index}
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    benefit.highlight 
                      ? 'bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800' 
                      : 'bg-gray-50 dark:bg-gray-800'
                  }`}
                >
                  <benefit.icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                    benefit.highlight ? 'text-amber-600' : 'text-gray-600 dark:text-gray-400'
                  }`} />
                  <span className={`text-sm ${
                    benefit.highlight 
                      ? 'text-amber-900 dark:text-amber-100 font-medium' 
                      : 'text-gray-700 dark:text-gray-300'
                  }`}>
                    {benefit.text}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Comparison */}
          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              Free vs Pro Comparison
            </h3>
            
            <div className="space-y-2">
              {[
                { feature: 'Access to templates', free: true, pro: true },
                { feature: 'Copy & download prompts', free: true, pro: true },
                { feature: 'Save prompts to cloud', free: false, pro: true },
                { feature: 'API keys required', free: 'Yes', pro: 'No' },
                { feature: 'Prompts limit', free: '0', pro: 'Unlimited' },
                { feature: 'Share prompts', free: false, pro: true },
                { feature: 'Priority support', free: false, pro: true },
              ].map((item, index) => (
                <div key={index} className="flex items-center justify-between text-sm">
                  <span className="text-gray-700 dark:text-gray-300">{item.feature}</span>
                  <div className="flex gap-8">
                    <span className="w-20 text-center">
                      {typeof item.free === 'boolean' ? (
                        item.free ? (
                          <Check className="w-4 h-4 text-green-500 inline" />
                        ) : (
                          <X className="w-4 h-4 text-gray-400 inline" />
                        )
                      ) : (
                        <span className="text-gray-500 dark:text-gray-400">{item.free}</span>
                      )}
                    </span>
                    <span className="w-20 text-center">
                      {typeof item.pro === 'boolean' ? (
                        item.pro ? (
                          <Check className="w-4 h-4 text-green-500 inline" />
                        ) : (
                          <X className="w-4 h-4 text-gray-400 inline" />
                        )
                      ) : (
                        <span className="text-amber-600 font-medium">{item.pro}</span>
                      )}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </>
          )}
        </div>

        {/* Footer */}
        {!showCheckout && (
          <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-slate-900">
            <button
              onClick={handleUpgrade}
              className="w-full py-3 px-6 bg-gradient-to-r from-amber-500 to-orange-500 text-white font-semibold rounded-xl hover:shadow-lg transform hover:scale-[1.02] transition-all duration-200"
            >
              Upgrade to Pro Now
            </button>
            <p className="text-xs text-center text-gray-500 dark:text-gray-400 mt-3">
              Cancel anytime. No questions asked.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpgradeModal;